import type { TriggerType, Tv2mSource } from '@apm/contracts';
import type { MessageKey } from '../i18n/ko.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 유입 배지. "이 화면을 왜 보고 있는가" 를 한 줄로 알려 준다.
 *
 * **v9 이전에는 트리거 제목을 `@apm/contracts` 의 `TV2M_NOTIFICATION_TITLES` 에서
 * 그대로 읽었다.** 그것이 한국어 한 벌이라 **영어 화면에 한국어가 그대로 나왔다** —
 * 절대 규칙 24 를 지나친 자리다. 지금은 사전을 거친다.
 *
 * 문구가 갈리는 것을 막는 장치는 둘로 나뉜다.
 *
 * - **한국어** — `badge.trigger*` 의 값이 `TV2M_NOTIFICATION_TITLES` 와 같은지
 *   `TriggerBadge.test.ts` 가 검사한다. 어긋나면 테스트가 깨진다
 * - **영어** — 짝은 `bloom-server` 의 `EN_TITLES` 이고 **타입으로 묶지 못한다**
 *   (앱이 서버를 import 하지 않는다). 계약에 언어 축을 더하면 `delegated` 파트너의
 *   계약까지 넓어지므로 그 길은 막혀 있다 (`docs/12` §2.3-b) — 규약으로 지킨다
 *
 * 두 표 모두 `Record<…, MessageKey>` 라 **값이 늘면 컴파일이 깨진다.** 빠뜨린 경로가
 * 빈 배지로 나가는 것보다 빌드가 멈추는 편이 낫다.
 */
const TRIGGER_KEYS: Record<TriggerType, MessageKey> = {
  ad: 'badge.triggerAd',
  channel_content: 'badge.triggerChannelContent',
  // v23. **광고가 아니라 장면이다.** `product.*` 를 재사용하지 않는 것과 같은 방침이다 —
  // 부르는 말이 갈리면 키도 갈린다 (절대 규칙 49).
  content_offer: 'badge.triggerContentOffer',
};

const SOURCE_KEYS: Record<Tv2mSource, MessageKey> = {
  tv2m: 'badge.tv2m',
  tv2m_same_device: 'badge.tv2mSameDevice',
  tv2m_deferred: 'badge.tv2mDeferred',
  tv2m_same_device_deferred: 'badge.tv2mSameDeviceDeferred',
};

export function TriggerBadge({
  source,
  triggerType,
}: {
  source: Tv2mSource;
  triggerType: TriggerType;
}) {
  const t = useT();
  return (
    <div className="badge-row">
      <span className="badge badge--accent">{t(TRIGGER_KEYS[triggerType])}</span>
      <span className="badge">{t(SOURCE_KEYS[source])}</span>
    </div>
  );
}
